<template>
  <div class="hot-searches-grid">
    <LocalizationProvider :language="kendoLang">
      <IntlProvider :locale="kendoLang">
        <Grid
          :loader="loader"
          :data-items="gridData"
          :resizable="true"
          :page-size="DEFAULT_TAKE"
          @sortchange="sortChangeHandler"
          :total="total"
          :take="take"
          :skip="skip"
          :sort="sort"
          :sortable="true"
          @pagechange="pageChangeHandler"
          :columns="columns"
          :pageable="pageable"
        >
          <GridToolbar>
            <div class="grid-toolbar">
              <div class="flex-fill d-flex align-items-center">
                <div>
                  <b-form-input
                    v-model="searchWord"
                    type="text"
                    :debounce="500"
                    :placeholder="$t('lbl_HotSearches_SearchPlaceholder')"
                  />
                </div>
                <div>
                  <b-button
                    variant="outline-secondary"
                    @click="onClickRefresh"
                    class="btn-refresh"
                  >
                    <i class="fa fa-refresh" />
                    {{ $t("lbl_EMPBE_Refresh") }}
                  </b-button>
                </div>
                <div class="ml-2">
                  <label class="filter-toggle" :class="{ 'filter-toggle--active': showOnlyActive }">
                    <input type="checkbox" v-model="localShowOnlyActive" />
                    <span class="filter-toggle-slider"></span>
                    <span class="filter-toggle-label">{{ $t("lbl_HotSearches_OnlyActive") }}</span>
                  </label>
                </div>
              </div>
              <div>
                <b-button variant="primary" @click="onClickAddKeyword" class="btn-add">
                  <i class="fa fa-plus-circle" />
                  {{ $t("lbl_HotSearches_AddKeyword") }}
                </b-button>
                <b-button variant="success" @click="onClickAddTag" class="btn-add ml-2">
                  <i class="fa fa-tag" />
                  {{ $t("lbl_HotSearches_AddTag") }}
                </b-button>
                <b-button variant="warning" @click="onClickImportTags" class="btn-add ml-2">
                  <i class="fa fa-upload" />
                  {{ $t("lbl_HotSearches_ImportTags") }}
                </b-button>
              </div>
            </div>
          </GridToolbar>

          <!-- Custom cells -->
          <template v-slot:typeCellTemplate="{ props }">
            <td>
              <span
                class="badge"
                :class="props.dataItem.type === 'keyword' ? 'badge-keyword' : 'badge-tag'"
              >
                {{ props.dataItem.type === "keyword" ? $t("lbl_HotSearches_Keyword") : $t("lbl_HotSearches_Tag") }}
              </span>
            </td>
          </template>

          <template v-slot:contentCellTemplate="{ props }">
            <td>
              <span v-if="props.dataItem.isTagDeleted" class="text-danger">
                <i class="fa fa-warning" />
                {{ props.dataItem.keyword || $t("lbl_HotSearches_TagDeleted") }}
              </span>
              <span v-else>
                {{ props.dataItem.keyword || props.dataItem.text || "-" }}
                <span
                  v-if="props.dataItem.type === 'tag' && (props.dataItem.tagId || props.dataItem.TagId)"
                  class="tag-id-hint"
                >
                  #{{ props.dataItem.tagId || props.dataItem.TagId }}
                </span>
              </span>
            </td>
          </template>

          <template v-slot:statusCellTemplate="{ props }">
            <td>
              <span
                class="badge"
                :class="statusBadgeClass(props.dataItem)"
              >
                {{ statusLabel(props.dataItem) }}
              </span>
            </td>
          </template>

          <template v-slot:dateFromCellTemplate="{ props }">
            <td>
              {{ formatDate(props.dataItem.effectiveDateFrom) }}
            </td>
          </template>

          <template v-slot:dateToCellTemplate="{ props }">
            <td>
              {{ formatDate(props.dataItem.effectiveDateTo) }}
            </td>
          </template>

          <template v-slot:commandsTemplate="{ props }">
            <td>
              <b-button
                variant="info"
                size="sm"
                @click="onClickEditRow(props.dataItem)"
              >
                {{ $t("lbl_EMPBE_Edit") }}
              </b-button>
              <b-button
                class="ml-2 "
                :variant="props.dataItem.isDelete ? 'success' : 'danger'"
                size="sm"
                @click="props.dataItem.isDelete ? onClickRestoreRow(props.dataItem) : onClickDeleteRow(props.dataItem)"
              >
                {{ props.dataItem.isDelete ? $t("lbl_EMPBE_Restore") : $t("lbl_EMPBE_Delete") }}
              </b-button>
            </td>
          </template>
        </Grid>
      </IntlProvider>
    </LocalizationProvider>

    <!-- Sidebar backdrop -->
    <div
      class="b-sidebar-backdrop bg-dark"
      style="z-index: 100"
      v-if="isShowForm"
      @click="onBackdropClick"
    ></div>

    <!-- Add/Edit Sidebar Form -->
    <b-sidebar
      id="hotSearchFormSidebar"
      right
      bg-variant="white"
      :shadow="true"
      v-model="isShowForm"
      ref="sideBarPopup"
      no-header
      :no-close-on-esc="isFormSaving"
      :no-close-on-route-change="true"
      body-class="overflow-hidden"
    >
      <div class="h-100 d-flex flex-column">
        <!-- Header -->
        <div class="sidebar-header">
          <div class="header-title-row">
            <span class="header-mode-icon" :class="isEditMode ? 'icon-edit' : 'icon-add'">
              <i :class="isEditMode ? 'fa fa-pencil' : 'fa fa-plus-circle'" />
            </span>
            <div class="header-title-group">
              <span class="header-title-text">
                {{ isEditMode ? $t("lbl_HotSearches_EditTitle") : $t("lbl_HotSearches_AddTitle") }}
              </span>
              <span class="header-subtitle">
                {{ isEditMode ? $t("lbl_HotSearches_EditSubtitle") : $t("lbl_HotSearches_AddSubtitle") }}
              </span>
            </div>
          </div>
          <button
            class="close-sidebar-btn"
            type="button"
            @click="onHideForm"
            :disabled="isFormSaving"
            :aria-label="$t('lbl_EMPBE_Cancel')"
          >
            <i class="fa fa-times" />
          </button>
        </div>

        <!-- Scrollable Form Body -->
        <div class="sidebar-body">
          <b-overlay :show="isFormSaving" rounded="sm">
            <ValidationObserver ref="formObserver" v-slot="{ invalid }">
              <form @submit.prevent="onSubmit">
                <!-- Section: Content -->
                <div class="form-section">
                  <div class="section-label">
                    {{ $t("lbl_HotSearches_Content") }}
                  </div>

                  <!-- Type 1: Custom Keyword -->
                  <ValidationProvider
                    v-if="formData.type === 'keyword'"
                    name="keyword"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <div class="form-group mb-3">
                      <label for="keywordInput">
                        {{ $t("lbl_HotSearches_KeywordText") }} <span class="text-danger">*</span>
                      </label>
                      <b-form-input
                        id="keywordInput"
                        v-model="formData.keyword"
                        :placeholder="$t('lbl_HotSearches_KeywordPlaceholder')"
                        maxlength="200"
                        @input="onKeywordInput"
                        class="form-input-lg"
                      />
                      <div class="input-footer">
                        <div class="char-count-bar">
                          <div class="char-count-progress" :style="{ width: (formData.keyword.length / 200) * 100 + '%' }" :class="{ 'char-count-progress--warn': formData.keyword.length >= 180 }"></div>
                        </div>
                        <div class="input-footer-row">
                          <span class="char-count" :class="{ 'char-count--warn': formData.keyword.length >= 180 }">
                            {{ formData.keyword.length }}/200
                          </span>
                          <b-form-invalid-feedback v-if="errors.length > 0" :state="false">
                            {{ errors[0] }}
                          </b-form-invalid-feedback>
                        </div>
                      </div>
                    </div>
                  </ValidationProvider>

                  <!-- Type 2: Tag Selection -->
                  <ValidationProvider
                    v-if="formData.type === 'tag'"
                    name="tag"
                    :rules="formData.tagId ? '' : 'required'"
                    v-slot="{ errors }"
                  >
                    <div class="form-group mb-3">
                      <label for="tagSelect">
                        {{ $t("lbl_HotSearches_SelectTag") }} <span class="text-danger">*</span>
                      </label>
                      <v-select
                        id="tagSelect"
                        v-model="selectedTagOption"
                        :options="filteredTagOptions"
                        :filterable="true"
                        :clearable="true"
                        :disabled="tagListLoading"
                        :loading="tagListLoading"
                        label="text"
                        value="value"
                        :placeholder="tagListLoading ? $t('lbl_HotSearches_LoadingTags') : $t('lbl_HotSearches_TagPlaceholder')"
                        :no-data-placeholder="$t('lbl_HotSearches_NoTagsFound')"
                        class="tag-select"
                      />
                      <div v-if="formData.tagId === -1" class="tag-deleted-warning">
                        <i class="fa fa-warning" />
                        {{ $t("lbl_HotSearches_PreviouslySelectedTag") }} {{ deletedTagId }}
                      </div>
                      <div class="input-footer">
                        <b-form-invalid-feedback v-if="errors.length > 0" :state="false">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </div>
                    </div>
                  </ValidationProvider>

                  <!-- Display Text -->
                  <div class="form-group mb-0">
                    <label for="displayText">
                      {{ $t("lbl_HotSearches_DisplayText") }}
                      <span class="helper-label">{{ $t("lbl_HotSearches_DisplayTextHelper") }}</span>
                    </label>
                    <b-form-input
                      id="displayText"
                      v-model="formData.keyword"
                      :placeholder="$t('lbl_HotSearches_DisplayTextPlaceholder')"
                      maxlength="200"
                      :readonly="true"
                      class="input-readonly"
                    />
                    <div class="input-footer">
                      <div class="char-count-bar">
                        <div class="char-count-progress" :style="{ width: (formData.keyword.length / 200) * 100 + '%' }" :class="{ 'char-count-progress--warn': formData.keyword.length >= 180 }"></div>
                      </div>
                      <div class="input-footer-row">
                        <span class="char-count" :class="{ 'char-count--warn': formData.keyword.length >= 180 }">
                          {{ formData.keyword.length }}/200
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Section: Scheduling -->
                <div class="form-section">
                  <div class="section-label">
                    {{ $t("lbl_HotSearches_Schedule") }}
                  </div>

                  <!-- Effective Date Range -->
                  <div class="form-group mb-0">
                    <label>{{ $t("lbl_HotSearches_EffectiveDateRange") }} <span class="text-danger">*</span></label>
                    <div class="date-range-row">
                      <div class="date-field">
                        <label class="date-field-label">{{ $t("lbl_HotSearches_EffectiveFrom") }}</label>
                        <datepicker_cp v-model="formData.effectiveDateFrom" />
                      </div>
                      <div class="date-separator">
                        <i class="fa fa-arrow-right" />
                      </div>
                      <div class="date-field">
                        <label class="date-field-label">{{ $t("lbl_HotSearches_EffectiveTo") }}</label>
                        <datepicker_cp v-model="formData.effectiveDateTo" />
                      </div>
                    </div>
                    <b-form-invalid-feedback v-if="!dateRangeValid" :state="false">
                      {{ $t("lbl_HotSearches_DateRangeError") }}
                    </b-form-invalid-feedback>
                  </div>
                </div>
              </form>
            </ValidationObserver>
          </b-overlay>
        </div>

        <!-- Fixed Footer -->
        <div class="sidebar-footer">
          <div class="footer-hints">
            <span class="footer-hint"><kbd>Esc</kbd> {{ $t("lbl_EMPBE_Cancel") }}</span>
          </div>
          <div class="footer-actions">
            <b-button
              variant="outline-secondary"
              @click="onHideForm"
              :disabled="isFormSaving"
              class="btn-cancel"
            >
              {{ $t("lbl_EMPBE_Cancel") }}
            </b-button>
            <b-button
              variant="primary"
              @click="onSubmit"
              :disabled="isFormSaving"
              class="btn-submit"
            >
              <span v-if="isFormSaving" class="spinner-inline">
                <i class="fa fa-spinner fa-spin mr-1" />
              </span>
              {{ isFormSaving
                ? (isEditMode ? $t("lbl_HotSearches_Updating") : $t("lbl_HotSearches_Creating"))
                : (isEditMode ? $t("lbl_HotSearches_Update") : $t("lbl_HotSearches_Create"))
              }}
            </b-button>
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- Empty State -->
    <div v-if="rawData.length === 0 && !loader" class="empty-state">
      <i class="fa fa-search empty-state-icon" />
      <p class="empty-state-text">{{ $t("lbl_HotSearches_EmptyState") }}</p>
      <div class="empty-state-actions">
        <b-button variant="primary" @click="onClickAddKeyword" class="mb-2">
          <i class="fa fa-plus-circle" />
          {{ $t("lbl_HotSearches_AddKeyword") }}
        </b-button>
        <b-button variant="success" @click="onClickAddTag">
          <i class="fa fa-tag" />
          {{ $t("lbl_HotSearches_AddTag") }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { IntlProvider, LocalizationProvider } from "@progress/kendo-vue-intl";
import { Grid, GridToolbar } from "@progress/kendo-vue-grid";
import { process } from "@progress/kendo-data-query";
import { hotSearchService } from "@/_services/hotSearch.service";
import { productService } from "@/_services/product.service";
import datepicker_cp from "@/components/form/datepicker_cp.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "HotSearchesGrid",
  components: {
    vSelect,
    Grid,
    GridToolbar,
    LocalizationProvider,
    IntlProvider,
    datepicker_cp,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    langId: {
      type: Number,
      required: true,
    },
    showOnlyActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rawData: [],
      loader: false,
      isShowForm: false,
      isEditMode: false,
      isFormSaving: false,
      searchWord: "",
      sort: [],
      total: 0,
      take: 10,
      skip: 0,
      pageSizeValue: 10,
      tagList: [],
      tagListLoading: false,
      deletedTagId: null,
      columns: [
        {
          field: "keyword",
          title: this.$t("lbl_HotSearches_Content"),
          cell: "contentCellTemplate",
          width: "200px",
        },
        {
          field: "type",
          title: this.$t("lbl_HotSearches_TypeLabel"),
          cell: "typeCellTemplate",
          width: "100px",
        },
        { field: "effectiveDateFrom", title: this.$t("lbl_HotSearches_EffectiveFrom"), width: "130px", cell: "dateFromCellTemplate" },
        { field: "effectiveDateTo", title: this.$t("lbl_HotSearches_EffectiveTo"), width: "130px", cell: "dateToCellTemplate" },
        {
          field: "status",
          title: this.$t("lbl_HotSearches_Status"),
          cell: "statusCellTemplate",
          width: "100px",
        },
        {
          title: this.$t("lbl_EMPBE_Action"),
          cell: "commandsTemplate",
          width: "220px",
          sortable: false,
        },
      ],
      formData: {
        hotSearchId: null,
        entryId: null,
        langId: null,
        tagId: null,
        keyword: "",
        sortOrder: 0,
        effectiveDateFrom: null,
        effectiveDateTo: null,
        type: "keyword",
      },
    };
  },
  watch: {
    langId: {
      handler() {
        this.loadData();
      },
      immediate: true,
    },
    showOnlyActive: {
      handler() {
        this.loadData();
      },
    },
    isShowForm: {
      handler() {
        if (this.isShowForm) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflowY = "scroll";
        }
      },
    },
  },
  computed: {
    kendoLang() {
      switch (this.langId) {
        case 1252:
          return "en-US";
        case 950:
          return "zh-Hant";
        case 936:
          return "zh-Hans";
        default:
          return "zh-Hans";
      }
    },
    DEFAULT_TAKE() {
      return 10;
    },
    pageable() {
      return {
        buttonCount: 5,
        info: true,
        type: "numeric",
        pageSizes: [10, 15, 50, "all"],
        previousNext: true,
        pageSizeValue: this.pageSizeValue,
      };
    },
    gridData() {
      let filteredData = [...this.rawData];

      if (this.searchWord) {
        const word = this.searchWord.toLowerCase();
        filteredData = filteredData.filter(
          (item) =>
            (item.keyword && item.keyword.toLowerCase().includes(word)) ||
            (item.text && item.text.toLowerCase().includes(word))
        );
      }

      this.total = filteredData.length;
      return process(filteredData, {
        take: this.take,
        skip: this.skip,
        sort: this.sort,
      });
    },
    tagOptions() {
      return [...this.tagList]
        .sort((a, b) => a.tagId - b.tagId)
        .map((tag) => ({
          value: tag.tagId,
          name: tag.text,
          text: `${tag.text} (ID: ${tag.tagId})`,
        }));
    },
    selectedTagOption: {
      get() {
        if (!this.formData.tagId) return null;
        return this.tagOptions.find((opt) => opt.value === this.formData.tagId) || null;
      },
      set(option) {
        this.formData.tagId = option ? option.value : null;
        if (option) {
          this.formData.keyword = option.name || "";
        }
      },
    },
    filteredTagOptions() {
      return this.tagOptions;
    },
    dateRangeValid() {
      if (!this.formData.effectiveDateFrom || !this.formData.effectiveDateTo) return false;
      return new Date(this.formData.effectiveDateTo) >= new Date(this.formData.effectiveDateFrom);
    },
    localShowOnlyActive: {
      get() {
        return this.showOnlyActive;
      },
      set(val) {
        this.$emit('update:showOnlyActive', val);
      },
    },
  },
  methods: {
    async loadData(force = false) {
      if (this.loader && !force) return;

      if (force) {
        this.skip = 0;
        this.take = this.DEFAULT_TAKE;
        this.sort = [];
        this.searchWord = "";
      }

      this.loader = true;
      try {
        const res = await hotSearchService.GetHotSearchesByLangId(this.langId, this.showOnlyActive);
        if (res.isSuccessful || res.IsSuccessful) {
          this.rawData = (res.data || []).map((item) => ({
            ...item,
            // Map API field names to component-internal names
            hotSearchId: item.hotSearchId || item.HotSearchId,
            entryId: item.entryId || item.EntryId || null,
            keyword: item.keyword || "",
            sortOrder: item.sortOrder || 0,
            isDelete: item.isDelete || item.IsDelete || false,
            type: (item.type || (item.tagId ? "tag" : "keyword")).toLowerCase(),
            status: this.computeStatus(item),
          }));

          // Sort: active items first, then by sortOrder ascending
          this.rawData.sort((a, b) => {
            const aActive = a.status === "Active" ? 0 : 1;
            const bActive = b.status === "Active" ? 0 : 1;
            if (aActive !== bActive) return aActive - bActive;
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          });

          // Load tag list once for tag picker
          if (this.tagList.length === 0) {
            await this.loadTagList();
          }
        }
      } catch (error) {
        // Failed to load hot searches — handled by upstream retry or user manual refresh
      } finally {
        this.loader = false;
      }
    },
    async loadTagList() {
      this.tagListLoading = true;
      try {
        const res = await productService.GetMstTag({ langId: this.langId });
        if (res.isSuccessful || res.IsSuccessful) {
          this.tagList = res.data || [];
        }
      } catch (error) {
        // Failed to load tag list — picker will show empty options
      } finally {
        this.tagListLoading = false;
      }
    },
    computeStatus(item) {
      if (item.isDelete || item.IsDelete) return "Deleted";
      const now = new Date();
      if (item.effectiveDateTo && new Date(item.effectiveDateTo) < now) return "Expired";
      if (item.effectiveDateFrom && new Date(item.effectiveDateFrom) > now) return "Scheduled";
      return "Active";
    },
    statusLabel(item) {
      const status = item.status || this.computeStatus(item);
      const map = {
        Active: this.$t("lbl_HotSearches_StatusActive"),
        Expired: this.$t("lbl_HotSearches_StatusExpired"),
        Scheduled: this.$t("lbl_HotSearches_StatusScheduled"),
        Deleted: this.$t("lbl_HotSearches_StatusDeleted"),
      };
      return map[status] || status;
    },
    statusBadgeClass(item) {
      const status = item.status || this.computeStatus(item);
      const map = {
        Active: "badge-active",
        Expired: "badge-expired",
        Scheduled: "badge-scheduled",
        Deleted: "badge-deleted",
      };
      return map[status] || "";
    },
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "-";
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    formatDateOnly(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    sortChangeHandler(event) {
      this.sort = event.sort;
    },
    pageChangeHandler(event) {
      this.skip = event.page.skip;
      this.take = event.event.value === "all" ? 1000 : event.page.take;
      this.pageSizeValue = event.event.value;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onClickRefresh() {
      this.searchWord = "";
      this.skip = 0;
      this.loadData();
    },
    onClickAddKeyword() {
      this.isEditMode = false;
      this.formData = {
        hotSearchId: null,
        langId: this.langId,
        tagId: null,
        keyword: "",
        sortOrder: 0,
        effectiveDateFrom: null,
        effectiveDateTo: null,
        type: "keyword",
      };
      this.deletedTagId = null;
      this.isShowForm = true;
    },
    onClickAddTag() {
      this.$swal({
        title: this.$t("lbl_HotSearches_AffectsAllLangs_Title"),
        text: this.$t("lbl_HotSearches_AffectsAllLangs_Text"),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then((res) => {
        if (!res.isConfirmed && !res.value) return;
        this.isEditMode = false;
        this.formData = {
          hotSearchId: null,
          langId: this.langId,
          tagId: null,
          keyword: "",
          sortOrder: 0,
          effectiveDateFrom: null,
          effectiveDateTo: null,
          type: "tag",
        };
        this.deletedTagId = null;
        this.isShowForm = true;
      });
    },
    onClickImportTags() {
      const guideTitle = this.$t("lbl_HotSearches_ImportGuide_Title");
      const guideTags = this.$t("lbl_HotSearches_ImportGuide_TagEntries");
      const guideKeywords = this.$t("lbl_HotSearches_ImportGuide_KeywordEntries");
      const guideLimit = this.$t("lbl_HotSearches_ImportGuide_Limit");
      const guideDates = this.$t("lbl_HotSearches_ImportGuide_Dates");
      const guideExample = this.$t("lbl_HotSearches_ImportGuide_Example");
      const placeholder = this.$t("lbl_HotSearches_ImportJsonPlaceholder");

      const html = `
        <style>
          .import-dialog { text-align: left; }
          .import-dialog .guide-section { margin-bottom: 16px; }
          .import-dialog .guide-title { font-weight: 600; font-size: 0.9rem; color: #303133; margin-bottom: 8px; }
          .import-dialog .guide-list { margin: 0 0 12px 0; padding-left: 20px; font-size: 0.85rem; color: #606266; line-height: 1.7; }
          .import-dialog .example-box {
            background: #f5f7fa; border: 1px solid #e4e7ed; border-radius: 6px;
            padding: 8px 12px; font-family: 'Consolas', 'Monaco', monospace; font-size: 0.8rem;
            color: #303133; line-height: 1.6; word-break: break-all;
          }
          .import-dialog .swal2-textarea {
            width: 100% !important;
            box-sizing: border-box !important;
            font-family: 'Consolas', 'Monaco', monospace !important;
            font-size: 0.85rem !important;
            min-height: 160px;
            border: 1px solid #dcdfe6 !important;
            border-radius: 6px !important;
            margin: 0 !important;
          }
          .import-dialog .swal2-textarea:focus {
            border-color: #409eff !important;
            outline: none;
            box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
          }
          .import-dialog .helper-text {
            font-size: 0.8rem; color: #909399; margin-top: 6px;
          }
        </style>
        <div class="import-dialog">
          <div class="guide-section">
            <div class="guide-title">${guideTitle}</div>
            <ul class="guide-list">
              <li>${guideTags}</li>
              <li>${guideKeywords}</li>
              <li>${guideDates}</li>
              <li>${guideLimit}</li>
            </ul>
            <div class="example-box">${guideExample}</div>
          </div>
          <textarea id="importJsonInput" class="swal2-textarea" placeholder="${placeholder}" rows="8"></textarea>
          <div class="helper-text">Accepted format: [{\"tagId\": number, \"effectiveDateFrom\": \"YYYY-MM-DD\", \"effectiveDateTo\": \"YYYY-MM-DD\"}, {\"keyword\": \"text\", \"langId\": number, \"effectiveDateFrom\": \"YYYY-MM-DD\", \"effectiveDateTo\": \"YYYY-MM-DD\"}]</div>
        </div>
      `;

      this.$swal({
        title: this.$t("lbl_HotSearches_ImportTags"),
        html: html,
        width: 600,
        padding: "1.5em",
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
        focusConfirm: false,
        returnFocus: true,
        didOpen: () => {
          const textarea = document.getElementById("importJsonInput");
          if (textarea) textarea.focus();
        },
        preConfirm: () => {
          const input = document.getElementById("importJsonInput");
          if (!input) return this.$swal.showValidationMessage("Internal error: textarea not found");
          const raw = input.value.trim();
          if (!raw) return this.$swal.showValidationMessage("Please enter JSON data");

          let parsed;
          try {
            parsed = JSON.parse(raw);
          } catch (e) {
            return this.$swal.showValidationMessage("Invalid JSON: " + e.message);
          }

          if (!Array.isArray(parsed)) return this.$swal.showValidationMessage("JSON must be an array");
          if (parsed.length === 0) return this.$swal.showValidationMessage("Array cannot be empty");
          if (parsed.length > 100) return this.$swal.showValidationMessage("Array exceeds maximum of 100 items");

          for (let i = 0; i < parsed.length; i++) {
            const item = parsed[i];
            if (typeof item !== "object" || item === null || Array.isArray(item)) {
              return this.$swal.showValidationMessage(`Item at index ${i} must be an object`);
            }
            if (!item.tagId && !item.keyword) {
              return this.$swal.showValidationMessage(`Item at index ${i} must have at least "tagId" or "keyword"`);
            }
            if (item.keyword && (typeof item.keyword !== "string" || item.keyword.trim().length === 0)) {
              return this.$swal.showValidationMessage(`Item at index ${i}: "keyword" cannot be empty`);
            }
            if (item.keyword && typeof item.keyword === "string" && item.keyword.length > 200) {
              return this.$swal.showValidationMessage(`Item at index ${i}: "keyword" exceeds 200 characters`);
            }
            if (item.tagId !== undefined && item.tagId !== null && typeof item.tagId !== "number") {
              return this.$swal.showValidationMessage(`Item at index ${i}: "tagId" must be a number`);
            }
            if (item.langId !== undefined && item.langId !== null) {
              if (typeof item.langId !== "number" || !Number.isInteger(item.langId) || item.langId <= 0) {
                return this.$swal.showValidationMessage(`Item at index ${i}: "langId" must be a positive integer (e.g. 1252, 950, 936)`);
              }
            }
            if (item.keyword && (!item.tagId || item.tagId === null) && (item.langId === undefined || item.langId === null)) {
              return this.$swal.showValidationMessage(`Item at index ${i}: "langId" is required for keyword entries (e.g. 1252, 950, 936)`);
            }
            if (!item.effectiveDateFrom) {
              return this.$swal.showValidationMessage(`Item at index ${i}: "effectiveDateFrom" is required (format: YYYY-MM-DD)`);
            }
            if (!item.effectiveDateTo) {
              return this.$swal.showValidationMessage(`Item at index ${i}: "effectiveDateTo" is required (format: YYYY-MM-DD)`);
            }
          }

          return parsed;
        },
      }).then(async (res) => {
        if (!res.isConfirmed || !res.value) return;
        const payload = res.value;

        this.loader = true;
        try {
          const result = await hotSearchService.CreateHotSearchBatch(payload);
          if (result.isSuccessful || result.IsSuccessful) {
            const data = result.data || {};
            const successCount = data.imported ?? 0;
            const failedCount = data.skipped ?? 0;
            const errors = data.errors || [];

            if (failedCount === 0) {
              this.$swal({
                text: this.$t("lbl_HotSearches_ImportSuccess", { count: successCount }),
                icon: "success",
              });
            } else {
              let detailText = `${successCount} of ${payload.length} entries imported successfully. ${failedCount} failed.`;
              if (errors.length > 0) {
                detailText += "\n\nErrors:\n" + errors.slice(0, 5).map(e => `Index ${e.index}: ${e.message}`).join("\n");
                if (errors.length > 5) detailText += "\n...and " + (errors.length - 5) + " more";
              }
              this.$swal({
                html: detailText.replace(/\n/g, "<br>"),
                icon: "warning",
              });
            }

            this.rawData = [];
            this.loader = false;
            await this.loadData();
            this.$emit("dataChanged", 1252);
            this.$emit("dataChanged", 950);
            this.$emit("dataChanged", 936);
          } else {
            this.$swal({
              text: result.systemErrorMsg || this.$t("lbl_HotSearches_ImportFailed"),
              icon: "error",
            });
          }
        } catch (error) {
          this.$swal({
            text: error?.response?.data?.systemErrorMsg || this.$t("lbl_HotSearches_ImportFailed"),
            icon: "error",
          });
        } finally {
          this.loader = false;
        }
      });
    },
    onClickEditRow(item) {
      const openEditForm = () => {
        this.isEditMode = true;
        this.formData = {
          hotSearchId: item.hotSearchId,
          entryId: item.entryId,
          langId: item.langId,
          tagId: item.tagId || null,
          keyword: item.keyword || "",
          sortOrder: item.sortOrder || 0,
          effectiveDateFrom: item.effectiveDateFrom || null,
          effectiveDateTo: item.effectiveDateTo || null,
          type: item.type || (item.tagId ? "tag" : "keyword"),
        };
        if (item.isTagDeleted) {
          this.deletedTagId = item.tagId;
          this.formData.tagId = -1;
        } else {
          this.deletedTagId = null;
        }
        this.isShowForm = true;
      };

      if (item.type === "tag") {
        this.$swal({
          title: this.$t("lbl_HotSearches_AffectsAllLangs_Title"),
          text: this.$t("lbl_HotSearches_AffectsAllLangs_EditText"),
          icon: "info",
          showCancelButton: true,
          confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
          cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
        }).then((res) => {
          if (!res.isConfirmed && !res.value) return;
          openEditForm();
        });
        return;
      }
      openEditForm();
    },
    onClickDeleteRow(item) {
      
      this.$swal({
        text: item.type === "tag" ? this.$t("lbl_HotSearches_AffectsAllLangs_DeleteText") : this.$t("lbl_EMPBE_SureToDelete"),
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then(async (res) => {
        if (!res.isConfirmed && !res.value) return;

        const entryId = item.entryId;
        if (!entryId) {
          console.warn('[HotSearches] Delete: missing entryId', item);
          this.$swal({ text: "Delete failed: missing EntryId", icon: "error" });
          return;
        }

        this.loader = true;
        try {
          const result = await hotSearchService.DeleteHotSearch({ entryId });
          if (result.isSuccessful || result.IsSuccessful) {
            this.$swal({
              text: this.$t("lbl_EMPBE_DeleteSuccess"),
            });
          } else {
            this.$swal({
              text: result.systemErrorMsg || this.$t("lbl_EMPBE_DeleteFailed"),
              icon: "error",
            });
            return;
          }
          this.rawData = [];
          this.loader = false;
          await this.loadData();
          // Notify all languages when deleting a tag
          if (item.type === "tag") {
            this.$emit("dataChanged", 1252);
            this.$emit("dataChanged", 950);
            this.$emit("dataChanged", 936);
          } else {
            this.$emit("dataChanged", this.langId);
          }
        } catch (error) {
          this.loader = false;
          this.$swal({
            text: this.$t("lbl_EMPBE_DeleteFailed"),
          });
        }
      });
    },
    onClickRestoreRow(item) {
      this.$swal({
        text: this.$t("lbl_HotSearches_AffectsAllLangs_RestoreText"),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then((res) => {
        if (!res.isConfirmed && !res.value) return;
        const entryId = item.entryId;

        if (!entryId) {
          this.$swal({ text: "Restore failed: missing EntryId", icon: "error" });
          return;
        }

        this.loader = true;
        try {
          hotSearchService.RestoreHotSearch({ entryId }).then((result) => {
            this.loader = false;
            if (result.isSuccessful || result.IsSuccessful) {
              this.$swal({ text: this.$t("lbl_EMPBE_RestoreSuccess") || "Restore success", icon: "success" });
            } else {
              this.$swal({
                text: result.systemErrorMsg || "Restore failed",
                icon: "error",
              });
              return;
            }
            this.rawData = [];
            this.loadData();
            this.$emit("dataChanged", 1252);
            this.$emit("dataChanged", 950);
            this.$emit("dataChanged", 936);
          }).catch(() => {
            this.loader = false;
            this.$swal({ text: "Restore failed", icon: "error" });
          });
        } catch (error) {
          this.loader = false;
          this.$swal({ text: "Restore failed", icon: "error" });
        }
      });
    },
    async onSubmit() {
      // Validate date range is required
      if (!this.formData.effectiveDateFrom || !this.formData.effectiveDateTo) {
        this.$swal({
          text: this.$t("lbl_HotSearches_DateRequired"),
        });
        return;
      }

      // Validate date range order
      if (!this.dateRangeValid) {
        this.$swal({
          text: this.$t("lbl_HotSearches_DateRangeError"),
        });
        return;
      }

      // Validate based on type
      if (this.formData.type === "keyword" && !this.formData.keyword.trim()) {
        this.$swal({
          text: this.$t("lbl_HotSearches_KeywordRequired"),
        });
        return;
      }

      if (this.formData.type === "tag" && !this.formData.tagId) {
        this.$swal({
          text: this.$t("lbl_HotSearches_TagRequired"),
        });
        return;
      }

      // Check for duplicate keyword within same language
      if (this.formData.type === "keyword") {
        const isDuplicate = this.rawData.some(
          (item) =>
            item.type === "keyword" &&
            item.keyword &&
            item.keyword.toLowerCase() === this.formData.keyword.trim().toLowerCase() &&
            item.hotSearchId !== this.formData.hotSearchId
        );
        if (isDuplicate) {
          this.$swal({
            text: this.$t("lbl_HotSearches_DuplicateKeyword"),
          });
          return;
        }
      }

      this.isFormSaving = true;

      try {
        const langIds = [this.langId];

        let allSuccess = true;
        for (const lid of langIds) {
          const payload = {
            langId: lid,
            tagId: this.formData.type === "tag" && this.formData.tagId > 0 ? this.formData.tagId : null,
            keyword: this.formData.keyword.trim(),
            sortOrder: this.formData.sortOrder || 0,
            effectiveDateFrom: this.formData.effectiveDateFrom
              ? this.formatDateOnly(this.formData.effectiveDateFrom)
              : null,
            effectiveDateTo: this.formData.effectiveDateTo
              ? this.formatDateOnly(this.formData.effectiveDateTo)
              : null,
            type: this.formData.type,
          };

          let result;
          if (this.isEditMode) {
            payload.EntryId = this.formData.entryId;
            payload.IsDelete = this.formData.isDelete || false;
            result = await hotSearchService.UpdateHotSearch(payload);
          } else {
            result = await hotSearchService.CreateHotSearch(payload);
          }

          if (!result.isSuccessful && !result.IsSuccessful) {
            console.error('[HotSearches] Create response error:', result);
            allSuccess = false;
            this.$swal({
              text: result.systemErrorMsg || this.$t("lbl_EMPBE_UpdateFailed"),
              icon: "error",
            });
            break;
          }
        }

        if (allSuccess) {
          this.$swal({
            text: this.$t(this.isEditMode ? "lbl_EMPBE_UpdateSuccess" : "lbl_EMPBE_SaveSuccess"),
          });
          this.onHideForm();
          await this.loadData();
          this.$emit("dataChanged", this.langId);
        }
      } catch (error) {
        console.error('[HotSearches] Create failed:', error);
        this.$swal({
          text: error?.response?.data?.systemErrorMsg || error?.message || this.$t("lbl_EMPBE_UpdateFailed"),
          icon: "error",
        });
      } finally {
        this.isFormSaving = false;
      }
    },
    onHideForm() {
      this.isShowForm = false;
      this.isEditMode = false;
    },
    formatDateOnly(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    onKeywordInput() {
      // Display text auto-syncs via shared v-model; method reserved for future validation hooks
    },
    isFormDirty() {
      if (!this.isShowForm) return false;
      return !!(
        this.formData.keyword ||
        this.formData.tagId ||
        this.formData.sortOrder ||
        this.formData.effectiveDateFrom ||
        this.formData.effectiveDateTo
      );
    },
    async onBackdropClick() {
      if (this.isFormDirty()) {
        const res = await this.$swal({
          text: this.$t("lbl_HotSearches_DiscardChanges"),
          showCancelButton: true,
          confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
          cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
        });
        if (!res.isConfirmed && !res.value) return;
      }
      this.onHideForm();
    },
  },
};
</script>

<style lang="scss" scoped>
.hot-searches-grid {
  position: relative;

  /* Kendo Grid cell alignment */
  :deep(.k-grid td) {
    vertical-align: middle;
    padding: 0.5rem 0.75rem;
  }

  :deep(.k-grid th.k-header) {
    vertical-align: middle;
    padding: 0.5rem 0.75rem;
  }

  :deep(.k-grid-toolbar) {
    padding: 0;
  }
}

/* Toolbar spacing */
.grid-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.5rem;
  flex-wrap: wrap;

  .flex-fill {
    gap: 0.5rem;
  }
}

/* Filter toggle switch */
.filter-toggle {
  margin:auto;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  transition: background-color 0.2s ease;

  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .filter-toggle-slider {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    border-radius: 10px;
    background-color: #d1d5db;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
      transition: transform 0.2s ease;
    }
  }

  .filter-toggle-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    transition: color 0.2s ease;
  }

  &.filter-toggle--active {
    background-color: #eef2ff;

    .filter-toggle-slider {
      background-color: #1e40af;

      &::after {
        transform: translateX(16px);
      }
    }

    .filter-toggle-label {
      color: #1e40af;
    }
  }

  &:hover {
    .filter-toggle-slider {
      background-color: #9ca3af;
    }

    &.filter-toggle--active .filter-toggle-slider {
      background-color: #2563eb;
    }
  }

  input:focus-visible + .filter-toggle-slider {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
}

/* Badge styles */
.badge {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.02em;
}

.badge-keyword {
  background-color: #4338ca;
  color: #ffffff;
}

.badge-tag {
  background-color: #059669;
  color: #ffffff;
}

.tag-id-hint {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #ffffff;
  background-color: #059669;
  border-radius: 4px;
  font-family: "SF Mono", "Roboto Mono", "Consolas", monospace;
  letter-spacing: 0.02em;
  vertical-align: text-bottom;
}

.badge-active {
  background-color: #059669;
  color: #ffffff;
}

.badge-expired {
  background-color: #dc2626;
  color: #ffffff;
}

.badge-scheduled {
  background-color: #d97706;
  color: #ffffff;
}

.badge-deleted {
  background-color: #9ca3af;
  color: #ffffff;
}

/* Toolbar buttons */
.btn-refresh,
.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.btn-refresh i,
.btn-add i {
  line-height: 1.5;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
}

.empty-state-icon {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state-text {
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
}

.empty-state-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

/* ===== Sidebar Form Styles ===== */

/* Header */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
  min-height: 64px;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.header-mode-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 1rem;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.header-mode-icon.icon-add {
  background: #eef2ff;
  color: #4338ca;
}

.header-mode-icon.icon-edit {
  background: #fff7ed;
  color: #c2410c;
}

.header-title-group {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.header-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
}

.header-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
}

/* Close button — improved size and accessibility */
.close-sidebar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: #6b7280;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background-color: #f3f4f6;
    color: #1f2937;
  }

  &:active {
    background-color: #e5e7eb;
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Scrollable body */
.sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
}

/* Form sections */
.form-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.section-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.625rem;
}

/* vue-select (tag searchable dropdown) */
.tag-select {
  .v-select {
    .vs__dropdown-toggle {
      border: 1px solid #ced4da;
      border-radius: 6px;
      min-height: 38px;
      padding: 4px 8px;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;

      &:focus-within {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }

    .vs__search {
      margin: 0;
      padding: 2px 4px;
      font-size: 0.875rem;
      color: #495057;

      &::placeholder {
        color: #9ca3af;
      }
    }

    .vs__selected {
      font-size: 0.875rem;
      color: #1f2937;
      border: none;
      padding: 2px 8px;
      margin: 2px 4px 2px 0;
      border-radius: 4px;
      background: #eef2ff;
    }

    .vs__clear svg {
      fill: #9ca3af;
    }

    .vs__dropdown-menu {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      padding: 4px 0;
      max-height: 280px;
      overflow-y: auto;
    }

    .vs__dropdown-option {
      padding: 6px 12px;
      font-size: 0.8125rem;
      line-height: 1.5;
      color: #374151;

      &.vs__dropdown-option--selected {
        background: #eef2ff;
        color: #1e40af;
        font-weight: 500;
      }

      &.vs__dropdown-option--highlight {
        background: #1e40af;
        color: #fff;
      }
    }

    .vs__no-options {
      font-size: 0.8125rem;
      color: #9ca3af;
      padding: 8px 12px;
    }

    &.vs--disabled {
      .vs__dropdown-toggle {
        background-color: #f3f4f6;
        cursor: not-allowed;
        opacity: 0.6;
      }

      .vs__search {
        pointer-events: none;
      }
    }
  }
}

/* Input footer (char count + validation) */
.input-footer {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.375rem;
}

.input-footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Character count with progress bar */
.char-count-bar {
  width: 100%;
  height: 3px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.char-count-progress {
  height: 100%;
  background: #3b82f6;
  border-radius: 2px;
  transition: width 0.15s ease, background-color 0.2s ease;

  &--warn {
    background: #f59e0b;
  }
}

.char-count {
  font-size: 0.75rem;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
  transition: color 0.2s ease;

  &--warn {
    color: #d97706;
    font-weight: 500;
  }
}

/* Helper label */
.helper-label {
  font-size: 0.75rem;
  font-weight: 400;
  color: #9ca3af;
  margin-left: 0.25rem;
}

/* Readonly input styling */
.input-readonly {
  background-color: #f9fafb;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Tag deleted warning */
.tag-deleted-warning {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  background: #fffbeb;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #b45309;
}

/* Date range row */
.date-range-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.date-field {
  flex: 1;
  min-width: 0;
}

.date-field-label {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.date-separator {
  display: flex;
  align-items: center;
  margin:auto;
  justify-content: center;
  height: calc(1.2em + 0.25rem);
  padding: 0;
  color: #d1d5db;
  font-size: 0.75rem;
}

/* Fixed footer */
.sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}

.footer-hints {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-hint {
  font-size: 0.6875rem;
  color: #9ca3af;

  kbd {
    display: inline-block;
    padding: 0.125rem 0.375rem;
    font-size: 0.625rem;
    font-family: inherit;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    box-shadow: 0 1px 0 #d1d5db;
    line-height: 1.4;
  }
}

.footer-actions {
  display: flex;
  gap: 0.625rem;

  .btn-cancel {
    flex: 0 0 auto;
    min-width: 100px;
  }

  .btn-submit {
    flex: 1;
    min-width: 140px;
  }

  .btn {
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.15s ease;
  }
}

.spinner-inline {
  display: inline-flex;
  align-items: center;
}
</style>
